// PRD 07 §4.2 / §8.1 — client-side validation for the Add/Edit form. Returns
// the localized error message per field, ready to render in the inline error
// span. Server-side validation (mock or BE) catches the same conditions and
// streams field errors back through `applyApiError` in the form panel.

import type { TFunction } from 'i18next';

import {
  firstBlockRuleViolation,
  withoutEmptyMediaBlocks,
  type BlockRuleCode,
} from '@/shared/utils/block-validation';

import type { ContentBlock, GuidelineGrade } from '../types/management-guidelines.types';

export type GuidelineFormFieldKey =
  | 'name'
  | 'category'
  | 'subArea'
  | 'linkedItem'
  | 'grade'
  | 'blocks';

export type GuidelineFormErrors = Partial<Record<GuidelineFormFieldKey, string>>;

export interface GuidelineFormDraft {
  name: string;
  category: string;
  subArea: string;
  linkedItem: string;
  grade: GuidelineGrade | null;
  blocks: ContentBlock[];
}

const NAME_MAX = 200;

/**
 * SHCT-370 Change 3 — PRD 07 §3 line 129 defines the block rules for 관리지침 but §8.1 names copy
 * for none of them except the video URL. The three content screens share one rule set and one block
 * editor (PRD 07 v1.9 "3곳 프로토 동일 구현"; 규격 canonical = PRD 10 §3.7), so each code takes the
 * message 상담매뉴얼 and 건강백과 already ship rather than a new wording invented here. Flagged to the
 * customer for ratification — see the SHCT-370 comment.
 */
const BLOCK_ERROR_KEYS: Record<BlockRuleCode, string> = {
  TOO_MANY_BLOCKS: 'errors.TOO_MANY_BLOCKS',
  EMPTY_BLOCK: 'errors.EMPTY_BLOCK',
  TEXT_BLOCK_TOO_LONG: 'errors.TEXT_BLOCK_TOO_LONG',
  VIDEO_URL_INVALID: 'errors.VIDEO_URL_INVALID',
  VIDEO_TITLE_INVALID: 'errors.VIDEO_TITLE_INVALID',
  IMAGE_TITLE_INVALID: 'errors.IMAGE_TITLE_INVALID',
  BLOCK_DESC_TOO_LONG: 'errors.BLOCK_DESC_TOO_LONG',
  EMOJI_NOT_ALLOWED: 'common:errors.emojiNotAllowed',
};

/**
 * PRD 07 §3 [SHCT-662] — 빈 미디어 블록은 저장 시 제외. The rule turned out to be identical on all
 * three content screens (PRD 07 v1.14 · 10 v2.13 · 11 v1.10), so it moved to the shared module;
 * this re-export keeps 관리지침's existing import sites and tests pointed at one name.
 */
export { withoutEmptyMediaBlocks };

export function validateGuidelineForm(
  draft: GuidelineFormDraft,
  t: TFunction,
): { ok: boolean; errors: GuidelineFormErrors } {
  const errors: GuidelineFormErrors = {};

  const name = draft.name.trim();
  if (!name) {
    errors.name = t('errors.NAME_REQUIRED');
  } else if (name.length > NAME_MAX) {
    errors.name = t('errors.NAME_TOO_LONG');
  }

  if (!draft.category) {
    errors.category = t('errors.CATEGORY_REQUIRED');
  }
  if (!draft.subArea) {
    errors.subArea = t('errors.SUB_AREA_REQUIRED');
  }
  if (!draft.linkedItem) {
    errors.linkedItem = t('errors.LINKED_ITEM_REQUIRED');
  }

  if (!draft.grade) {
    errors.grade = t('errors.GRADE_REQUIRED');
  }

  // PRD 07 §3 line 129 — 텍스트 블록 저장 검증(순수텍스트 1~500자, 빈/공백만 무효), 영상 URL, 블록 수.
  // Validated against the SAME array the save sends, so an empty media block that §3 [SHCT-662] excludes
  // cannot trip the EMPTY_BLOCK rule. 블록 0개도 저장 가능 (§4.2), so an empty list is simply skipped.
  const blocks = withoutEmptyMediaBlocks(draft.blocks);
  if (blocks.length > 0) {
    const violation = firstBlockRuleViolation(blocks);
    if (violation) {
      errors.blocks = t(BLOCK_ERROR_KEYS[violation.code], { max: violation.max });
    }
  }

  return { ok: Object.keys(errors).length === 0, errors };
}
